// Image Configuration Utility
// Provides environment-aware URLs for images
import React from 'react';

// Configuration for different environments
const CONFIG = {
  // Production domain URL
  PRODUCTION_BASE_URL: 'https://marcodemarco.net',
  
  // Development: use production domain for images since we don't serve them locally
  DEVELOPMENT_BASE_URL: 'https://marcodemarco.net'
};

/**
 * Gets the appropriate base URL for the current environment
 * @returns {string} Base URL for images and manifests
 */
export const getBaseUrl = () => {
  // In development, always use production URL for images
  if (process.env.NODE_ENV === 'development') {
    console.log('Development mode: Using production CloudFront URL for images');
    return CONFIG.DEVELOPMENT_BASE_URL;
  }
  
  // In production, use relative URLs (served from same domain)
  return '';
};

/**
 * Gets the full URL for an image or manifest
 * @param {string} path - Relative path (e.g., '/images/galleries/wedding/photo.jpg')
 * @returns {string} Full URL
 */
export const getImageUrl = (path) => {
  // If path is already a full URL (http/https), return as-is
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }
  
  const baseUrl = getBaseUrl();
  return baseUrl ? `${baseUrl}${path}` : path;
};

/**
 * Gets the thumbnail URL for an image
 * @param {string} imageSrc - Original image source
 * @returns {string} Thumbnail URL
 */
export const getThumbnailUrl = (imageSrc) => {
  // Extract the filename from the path
  const filename = imageSrc.split('/').pop();
  
  // If it's already a full URL, convert to thumbnail
  if (imageSrc.startsWith('http')) {
    return imageSrc.replace(/\/([^/]+)$/, `/thumbnails/${filename}`);
  }
  
  // For relative URLs, add thumbnails path
  return imageSrc.replace(/\/([^/]+)$/, `/thumbnails/${filename}`);
};

/**
 * Gets the optimized URL for an image
 * @param {string} imageSrc - Original image source
 * @returns {string} Optimized URL
 */
export const getOptimizedUrl = (imageSrc) => {
  // Extract the filename from the path
  const filename = imageSrc.split('/').pop();
  
  // If it's already a full URL, convert to optimized
  if (imageSrc.startsWith('http')) {
    return imageSrc.replace(/\/([^/]+)$/, `/optimized/${filename}`);
  }
  
  // For relative URLs, add optimized path
  return imageSrc.replace(/\/([^/]+)$/, `/optimized/${filename}`);
};

/**
 * Processes gallery data to use environment-aware URLs
 * @param {Object} galleryData - Gallery data object
 * @returns {Object} Processed gallery data with environment-aware URLs
 */
export const processGalleryData = (galleryData) => {
  const processed = {};
  
  Object.keys(galleryData).forEach(galleryName => {
    processed[galleryName] = galleryData[galleryName].map(photo => ({
      ...photo,
      src: getImageUrl(photo.src)
    }));
  });
  
  return processed;
};

/**
 * React hook to get environment-aware gallery data
 * @param {Object} galleryData - Raw gallery data
 * @returns {Object} Processed gallery data
 */
export const useGalleryData = (galleryData) => {
  return React.useMemo(() => processGalleryData(galleryData), [galleryData]);
};

const imageConfig = {
  getBaseUrl,
  getImageUrl,
  processGalleryData
};

export default imageConfig;
